// src/utils/firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  getDocs,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { getAuth, onAuthStateChanged, signInAnonymously } from "firebase/auth";

/* ============ CẤU HÌNH DÁN TỪ FIREBASE CONSOLE ============ */
const firebaseConfig = {
  apiKey: "AIzaSyBCy-z6El77XvgZ9OQQw20mExmUtGfZyqU",
  authDomain: "fir-b3962.firebaseapp.com",
  projectId: "fir-b3962",
  storageBucket: "fir-b3962.firebasestorage.app",
  messagingSenderId: "833027620749",
  appId: "1:833027620749:web:a82b0cd6ab03ad95c887c4",
};

/* ====== Debug ngắn gọn (tuỳ chọn) ====== */
try {
  // eslint-disable-next-line no-console
  console.log("[FIREBASE CFG]", {
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.slice(0, 6)}…` : null,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  });
} catch {}

/* =============== Safe initialization (no re-init on HMR) =============== */
let app = null;
let db = null;
let storage = null;
let auth = null;

try {
  app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  db = getFirestore(app);
  storage = getStorage(app);
  auth = getAuth(app);
} catch (e) {
  // eslint-disable-next-line no-console
  console.error("[Firebase] initializeApp failed:", e);
  app = null;
  db = null;
  storage = null;
  auth = null;
}

/* =============== Exports nền tảng =============== */
export { app };
export { db, storage };
export const serverTS = serverTimestamp;
export { auth };

/* =============== Auth tiện ích =============== */
// Promise resolve khi auth có trạng thái user (có thể null). Nếu chưa init auth thì resolve null.
export const whenAuthReady = new Promise((resolve) => {
  if (!auth) return resolve(null);
  onAuthStateChanged(auth, (u) => resolve(u || null));
});

/** Lấy user hiện tại (có thể null) */
export const getCurrentUser = () => {
  try {
    return auth?.currentUser || null;
  } catch {
    return null;
  }
};

/** Lấy uid hiện tại (có thể null) */
export const getCurrentUid = () => {
  const u = getCurrentUser();
  return u?.uid || null;
};

/**
 * Đảm bảo có phiên đăng nhập (ẩn danh) trước khi gọi Firestore/Storage cho phần chat.
 * LƯU Ý: KHÔNG auto gọi hàm này lúc import file nữa (tránh signInAnonymously fail nếu provider Anonymous chưa bật).
 */
export async function ensureSignedIn() {
  if (!auth) {
    // eslint-disable-next-line no-console
    console.warn("[Firebase] auth chưa sẵn sàng (auth=null).");
    return;
  }
  if (auth.currentUser) return;

  try {
    await signInAnonymously(auth);
  } catch (e) {
    // Nếu bạn CHƯA bật Anonymous provider thì lỗi này là bình thường -> bỏ qua để không làm rối login email/password
    // eslint-disable-next-line no-console
    console.warn("signInAnonymously failed (ignored):", e?.code || e?.message);
  }
}

// ✅ QUAN TRỌNG: KHÔNG gọi ensureSignedIn() tự động ở đây nữa
// ensureSignedIn();

/* =============== Helpers chung =============== */
export const convIdOf = (username) =>
  String(username || "guest")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* =============== Guards =============== */
const guardDB = () => {
  if (!db) {
    // eslint-disable-next-line no-console
    console.warn("[Firebase] Firestore chưa sẵn sàng. Bỏ qua thao tác để tránh crash UI.");
    return false;
  }
  return true;
};

const guardStorage = () => {
  if (!storage) {
    // eslint-disable-next-line no-console
    console.warn("[Firebase] Storage chưa sẵn sàng. Bỏ qua thao tác để tránh crash UI.");
    return false;
  }
  return true;
};

/* =========================================================================
   ✅ USER PREFS (mỗi user ghi nhớ riêng theo UID)
   - Lưu tại: users/{uid}/prefs/main
   - KHÔNG auto signInAnonymously (chỉ dùng user đăng nhập thật)
   - Dùng cho: Arbitrage groups, Notes, Favorites, UI state...
   ========================================================================= */

const PREFS_DOC_PATH = (uid) => doc(db, "users", String(uid), "prefs", "main");

/** Load prefs của user (return object hoặc null) */
export async function loadUserPrefs(uid) {
  if (!guardDB()) return null;
  if (!uid) return null;

  try {
    const ref = PREFS_DOC_PATH(uid);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[Firebase] loadUserPrefs failed:", e?.code || e?.message);
    return null;
  }
}

/**
 * Save prefs (merge) cho user
 * patch là object partial, ví dụ:
 * { arbitrage: { groupA: [...], groupB: [...] } }
 */
export async function saveUserPrefs(uid, patch) {
  if (!guardDB()) return;
  if (!uid) return;
  if (!patch || typeof patch !== "object") return;

  try {
    const ref = PREFS_DOC_PATH(uid);
    await setDoc(
      ref,
      { ...(patch || {}), updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[Firebase] saveUserPrefs failed:", e?.code || e?.message);
  }
}

/**
 * Listen realtime prefs của user
 * handler(data|null)
 */
export function listenUserPrefs(uid, handler) {
  if (!guardDB()) return () => {};
  if (!uid) return () => {};
  const ref = PREFS_DOC_PATH(uid);

  return onSnapshot(
    ref,
    (snap) => {
      handler(snap.exists() ? snap.data() : null);
    },
    (err) => {
      // eslint-disable-next-line no-console
      console.warn("[Firebase] listenUserPrefs error:", err?.code || err?.message);
      handler(null);
    }
  );
}

/* =============== Chat APIs (giữ nguyên tính năng) =============== */
// cập nhật meta hội thoại + thêm message (text)
export async function sendMessage(
  conversationId,
  { text, userId, username, role = "user" }
) {
  if (!guardDB()) return;
  if (!conversationId) throw new Error("conversationId is required");

  await ensureSignedIn();

  // Always use the real Firebase auth uid for ownership — never the caller-supplied
  // userId which may be a visitor-{uuid} string that doesn't match auth.uid.
  const authUid = auth?.currentUser?.uid || null;
  if (role === "user" && !authUid) {
    throw new Error("auth_uid_required_for_user_chat");
  }

  const convRef = doc(db, "conversations", conversationId);

  // userId written only when role === "user" so the conversation doc always
  // carries the owner's Firebase auth uid. Admin replies (merge:true) don't
  // overwrite this field.
  const convMeta = {
    id: conversationId,
    username: username || null,
    lastText: String(text || ""),
    lastFrom: role, // 'user' | 'admin'
    updatedAt: serverTimestamp(),
  };
  if (role === "user") {
    convMeta.userId = authUid;
  }

  await setDoc(convRef, convMeta, { merge: true });

  await addDoc(collection(convRef, "messages"), {
    text: String(text || ""),
    type: "text",
    role,
    userId: role === "user" ? authUid : (userId || null),
    username: username || null,
    createdAt: serverTimestamp(),
  });
}

// Gửi ảnh
export async function sendImageMessage(
  conversationId,
  file,
  { userId, username, role = "user" } = {}
) {
  if (!guardDB()) return null;
  if (!guardStorage()) return null;
  if (!conversationId) throw new Error("conversationId is required");
  if (!file) return null;

  await ensureSignedIn();

  // Always use the real Firebase auth uid — never the caller-supplied userId.
  const authUid = auth?.currentUser?.uid || null;
  if (role === "user" && !authUid) {
    throw new Error("auth_uid_required_for_user_chat");
  }

  const path = `chat_images/${conversationId}/${Date.now()}_${file.name}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file);
  const url = await getDownloadURL(ref);

  const convRef = doc(db, "conversations", conversationId);

  const imgConvMeta = {
    id: conversationId,
    username: username || null,
    lastText: "[ảnh]",
    lastFrom: role,
    updatedAt: serverTimestamp(),
  };
  if (role === "user") {
    imgConvMeta.userId = authUid;
  }

  await setDoc(convRef, imgConvMeta, { merge: true });

  await addDoc(collection(convRef, "messages"), {
    text: url,
    type: "image",
    role,
    userId: role === "user" ? authUid : (userId || null),
    username: username || null,
    createdAt: serverTimestamp(),
  });

  return url;
}

/** Gửi tin nhắn hệ thống (role = 'system'), KHÔNG đổi lastFrom */
export async function sendSystemMessage(conversationId, text) {
  if (!guardDB()) return;
  if (!conversationId) throw new Error("conversationId is required");

  await ensureSignedIn();

  const convRef = doc(db, "conversations", conversationId);

  await addDoc(collection(convRef, "messages"), {
    text: String(text || ""),
    type: "system",
    role: "system",
    createdAt: serverTimestamp(),
  });

  await setDoc(
    convRef,
    {
      id: conversationId,
      lastText: String(text || ""),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// lắng nghe danh sách message realtime
export function listenMessages(conversationId, handler) {
  if (!guardDB()) return () => {};
  const q = query(
    collection(db, "conversations", conversationId, "messages"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    handler(items);
  });
}

// lắng nghe danh sách hội thoại (admin)
export function listenConversations(handler, onError) {
  if (!guardDB()) return () => {};
  const q = query(collection(db, "conversations"), orderBy("updatedAt", "desc"));
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      handler(items);
    },
    (err) => {
      if (typeof onError === "function") onError(err);
      // eslint-disable-next-line no-console
      console.warn("[Firebase] listenConversations error:", err?.code || err?.message);
    }
  );
}

// xoá toàn bộ hội thoại + messages
export async function deleteConversation(conversationId) {
  if (!guardDB()) return;

  await ensureSignedIn();

  const convRef = doc(db, "conversations", conversationId);
  const msgsSnap = await getDocs(collection(convRef, "messages"));
  const batch = [];
  msgsSnap.forEach((d) => batch.push(deleteDoc(d.ref)));
  await Promise.all(batch);
  await deleteDoc(convRef);
}